import { useState, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowUpDownIcon, EllipsisVerticalIcon, PencilIcon, Trash2Icon, XIcon } from 'lucide-react'
import {
	ConfirmDialog,
	Alert, AlertDescription,
	Avatar, AvatarFallback,
	Button,
	Dialog, DialogClose, DialogContent, DialogTitle,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
	Form, FormControl, FormField, FormItem, FormLabel,
	IconButton,
	Input,
	PanelFilters,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
	documentTemplateCreateSchema,
	useCreateDocumentTemplateMutation,
	useDeleteDocumentTemplateMutation,
	useListDocumentTemplatesQuery,
	useListTemplateAuthorsQuery,
	useListTemplateHistoryQuery,
	useListTemplateVersionsQuery,
	useUpdateDocumentTemplateMutation,
} from '@/api'
import { PanelDown } from '@/components/panel-down'
import { TemplateEditor } from '@/components/template-editor'
import { TemplatePreviewDialog } from '@/components/template-preview'
import type { TemplateHistoryEntry, TemplateVersion } from '@/components/template-preview'
import { DEFAULT_LOCALE, DOCUMENT_TYPES, DOCUMENT_TYPE_KEYS } from '@/types'
import type { DocumentTemplate, DocumentType, TemplateBlock } from '@/types'

export const Route = createFileRoute('/_app/documents/templates')({
	component: DocumentsTemplatesPage,
})

const PAGE_SIZE = 10

// Form schema reuses the wire-level `title` rule from the API DTO so that
// client-side validation stays in lockstep with backend constraints.
// `baseTemplate` is a UI-only field (resolved into a real DocumentTemplate later).
const createTemplateFormSchema = documentTemplateCreateSchema
	.pick({ title: true })
	.extend({ baseTemplate: z.string().optional() })

type CreateTemplateFormValues = z.infer<typeof createTemplateFormSchema>

function CreateTemplateDialog({ open, onOpenChange, onNext, templates }: {
	open: boolean
	onOpenChange: (v: boolean) => void
	onNext: (title: string, baseTemplate: DocumentTemplate | null) => void
	templates: DocumentTemplate[]
}) {
	const { t } = useTranslation()
	const existingTemplates = templates
	const form = useForm<CreateTemplateFormValues>({
		resolver:      zodResolver(createTemplateFormSchema),
		defaultValues: { baseTemplate: '', title: '' },
		mode:          'onBlur',
	})

	function onSubmit(values: CreateTemplateFormValues) {
		const base = existingTemplates.find((tpl) => tpl.id === values.baseTemplate) ?? null
		onNext(values.title, base)
		form.reset()
		onOpenChange(false)
	}

	function handleOpenChange(next: boolean) {
		if (!next) form.reset()
		onOpenChange(next)
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				showCloseButton={false}
				className="sm:max-w-[28rem] w-full p-0 gap-0 rounded-lg overflow-hidden"
			>
				<div className="flex items-start justify-between p-6 pb-2">
					<div>
						<DialogTitle className="text-xl font-semibold leading-7">
							{t('documents.createDialog.title')}
						</DialogTitle>
						<p className="mt-1 text-sm text-(--color-muted-foreground)">
							{t('documents.createDialog.description')}
						</p>
					</div>
					<DialogClose
						render={
							<button
								type="button"
								className="rounded-(--radius-sm) p-0.5 text-(--color-muted-foreground) hover:text-(--color-foreground) transition-colors"
							/>
						}
					>
						<XIcon className="size-4" />
					</DialogClose>
				</div>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="flex flex-col gap-5 px-6 pt-2 pb-6">
							<FormField
								control={form.control}
								name="baseTemplate"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('documents.createDialog.baseTemplate')}</FormLabel>
										<Select onValueChange={field.onChange} value={field.value}>
											<FormControl>
												<SelectTrigger className="h-auto px-4 py-[10px] text-sm">
													<SelectValue placeholder={t('documents.createDialog.baseTemplatePlaceholder')} />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{existingTemplates.length > 0
													? existingTemplates.map((tpl) => (
														<SelectItem key={tpl.id} value={tpl.id}>{tpl.title}</SelectItem>
													))
													: <SelectItem value="_empty" disabled>{t('documents.templates.empty')}</SelectItem>
												}
											</SelectContent>
										</Select>
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="title"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('documents.createDialog.templateName')}</FormLabel>
										<FormControl>
											<Input placeholder={t('documents.createDialog.templateNamePlaceholder')} {...field} />
										</FormControl>
									</FormItem>
								)}
							/>
						</div>

						<div className="flex items-center justify-end gap-2 px-6 pb-6">
							<Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
								{t('documents.createDialog.cancel')}
							</Button>
							<Button type="submit">
								{t('documents.createDialog.next')}
							</Button>
						</div>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function DocumentsTemplatesPage() {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')
	const [page, setPage] = useState(1)
	const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all')
	const [authorFilter, setAuthorFilter] = useState<string>('all')
	const [createOpen, setCreateOpen] = useState(false)
	const [editorOpen, setEditorOpen] = useState(false)
	const [editorName, setEditorName] = useState('')
	const [editorBaseName, setEditorBaseName] = useState('')
	// null = creating new template; string = ID of existing template being edited
	const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null)
	// used only when creating from a base template (editingTemplateId is null)
	const [editorBaseId, setEditorBaseId] = useState<string>('')
	const [editorType, setEditorType] = useState<DocumentType>('OTHER')
	const [editorInitialBlocks, setEditorInitialBlocks] = useState<TemplateBlock[]>([])
	const [previewTemplate, setPreviewTemplate] = useState<DocumentTemplate | null>(null)
	const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplate | null>(null)
	const [deleteError, setDeleteError] = useState<string | null>(null)

	const [createTemplate, { isLoading: isCreating }] = useCreateDocumentTemplateMutation()
	const [updateTemplate, { isLoading: isUpdating }] = useUpdateDocumentTemplateMutation()
	const [deleteTemplate, { isLoading: isDeleting }] = useDeleteDocumentTemplateMutation()

	const { data: versionsData } = useListTemplateVersionsQuery(previewTemplate?.id ?? '', {
		skip: !previewTemplate,
	})

	const versions: TemplateVersion[] = (versionsData?.versions ?? [])
		.slice()
		.sort((a, b) => b.versionNumber - a.versionNumber)
		.map((v, i) => ({
			id:        v.id,
			label:     t('documents.preview.versionLabel', { number: v.versionNumber }),
			isCurrent: i === 0,
		}))

	const { data: historyData } = useListTemplateHistoryQuery(previewTemplate?.id ?? '', {
		skip: !previewTemplate,
	})

	const history: TemplateHistoryEntry[] = (historyData?.entries ?? [])
		.filter((entry) => entry.kind === 'created' || entry.kind === 'modified')
		.map((entry) => ({ id: entry.id, kind: entry.kind as 'created' | 'modified', at: entry.at }))

	function handlePreviewEdit() {
		if (!previewTemplate) return
		setEditorName(previewTemplate.title)
		setEditorBaseName('')
		setEditingTemplateId(previewTemplate.id)
		setEditorBaseId('')
		setEditorType(previewTemplate.type)
		setEditorInitialBlocks(previewTemplate.blocks ?? [])
		setEditorOpen(true)
		setPreviewTemplate(null)
	}

	async function handleEditorSave(blocks: TemplateBlock[]) {
		if (editingTemplateId) {
			await updateTemplate({
				id:   editingTemplateId,
				body: { title: editorName, type: editorType, blocks },
			}).unwrap()
		} else {
			await createTemplate({
				title:          editorName,
				type:           editorType,
				baseTemplateId: editorBaseId || undefined,
				blocks,
			}).unwrap()
		}
		setEditorInitialBlocks([])
		setEditingTemplateId(null)
	}

	function handleEditTemplate(tpl: DocumentTemplate) {
		setEditorName(tpl.title)
		setEditorBaseName('')
		setEditingTemplateId(tpl.id)
		setEditorBaseId('')
		setEditorType(tpl.type)
		setEditorInitialBlocks(tpl.blocks ?? [])
		setEditorOpen(true)
	}

	function handleDownloadPdf() {
		if (!previewTemplate?.blocks?.length) return
		const sorted = [...previewTemplate.blocks].sort((a, b) => a.blockOrder - b.blockOrder)
		let body = `<h1 style="text-align:center;font-size:14px;font-weight:bold;margin-bottom:32px">${previewTemplate.title}</h1>`
		for (const block of sorted) {
			if (block.type === 'SECTION') {
				body += `<h2 style="font-size:13px;font-weight:bold;text-transform:uppercase;letter-spacing:0.05em;margin:20px 0 8px">${block.content ?? ''}</h2>`
			} else if (block.type === 'SUBSECTION') {
				body += `<h3 style="font-size:13px;font-weight:600;margin:12px 0 6px">${block.content ?? ''}</h3>`
			} else if (block.type === 'TEXT') {
				body += `<p style="margin:0 0 8px;text-align:justify">${(block.content ?? '').replace(/\n/g, '<br/>')}</p>`
			} else if (block.type === 'FIELD') {
				body += `<span style="border-bottom:1px dashed #999;display:inline-block;min-width:120px;margin:0 2px">${block.content ?? ''}</span>`
			}
		}
		const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${previewTemplate.title}</title><style>body{font-family:"Times New Roman",Times,serif;font-size:13px;line-height:1.65;padding:40px 60px;max-width:720px;margin:auto}@media print{@page{margin:20mm}}</style></head><body>${body}</body></html>`
		const win = window.open('', '_blank')
		if (win) {
			win.document.write(html)
			win.document.close()
			win.focus()
			setTimeout(() => win.print(), 250)
		}
	}

	async function handleConfirmDelete() {
		if (!templateToDelete) return
		setDeleteError(null)
		try {
			await deleteTemplate(templateToDelete.id).unwrap()
			setTemplateToDelete(null)
		} catch (err) {
			const msg = err instanceof Error ? err.message : t('documents.templates.deleteError')
			setDeleteError(msg)
		}
	}

	const { data: authorsData } = useListTemplateAuthorsQuery()
	const authorOptions = authorsData?.authors ?? []

	const { data: allTemplatesData } = useListDocumentTemplatesQuery({ page: 1, limit: 1000 })
	const availableTypes = useMemo(() => {
		const usedTypes = new Set((allTemplatesData?.templates ?? []).map((t) => t.type))
		return DOCUMENT_TYPES.filter((dt) => usedTypes.has(dt))
	}, [allTemplatesData])

	const { data } = useListDocumentTemplatesQuery({
		search: search || undefined,
		page:   1,
		limit:  1000,
	})

	const filteredTemplates = useMemo(() => {
		let all = data?.templates ?? []
		if (typeFilter !== 'all') all = all.filter((t) => t.type === typeFilter)
		if (authorFilter !== 'all') all = all.filter((t) => t.createdBy === authorFilter)
		return all
	}, [data, typeFilter, authorFilter])

	const templates = filteredTemplates.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
	const total = filteredTemplates.length

	return (
		<div className="flex h-full flex-col">
			<PanelFilters
				filters={
					<>
						<Select
							value={typeFilter}
							onValueChange={(v) => { setTypeFilter(v as DocumentType | 'all'); setPage(1) }}
						>
							<SelectTrigger width="md" tone="muted">
								<SelectValue placeholder={t('documents.templates.filterType')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('documents.templates.allTypes')}</SelectItem>
								{availableTypes.map((dt) => (
									<SelectItem key={dt} value={dt}>{t(DOCUMENT_TYPE_KEYS[dt])}</SelectItem>
								))}
							</SelectContent>
						</Select>
						<Select
							value={authorFilter}
							onValueChange={(v) => { setAuthorFilter(v); setPage(1) }}
						>
							<SelectTrigger width="lg" tone="muted">
								<SelectValue placeholder={t('documents.templates.filterAuthor')} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">{t('documents.templates.allAuthors')}</SelectItem>
								{authorOptions.map((opt) => (
									<SelectItem key={opt.id} value={opt.id}>{opt.name}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				}
				actions={
					<>
						<SearchInput
							placeholder={t('documents.search')}
							value={search}
							className="w-[200px]"
							onChange={(e) => { setSearch(e.target.value); setPage(1) }}
						/>
						<Button size="sm" onClick={() => setCreateOpen(true)}>{t('documents.templates.create')}</Button>
					</>
				}
			/>

			{templates.length === 0 ? (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-sm text-(--color-muted-foreground)">{t('documents.templates.empty')}</p>
				</div>
			) : (
				<>
					<div className="flex min-h-0 flex-1 flex-col overflow-auto px-4">
						<div className="overflow-hidden rounded-lg border border-(--color-border)">
							<Table>
								<TableHeader>
									<TableRow className="hover:bg-transparent">
										<TableHead>
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.templates.columnTemplate')}
											</Button>
										</TableHead>
										<TableHead>
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.templates.columnAuthor')}
											</Button>
										</TableHead>
										<TableHead>
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.templates.columnCreatedAt')}
											</Button>
										</TableHead>
										<TableHead>
											<Button
												variant="ghost"
												iconRight={<ArrowUpDownIcon className="size-3.5" />}
												className="h-auto px-1 py-0.5 gap-1 font-[500] text-(--color-muted-foreground) hover:bg-(--color-muted)/60"
											>
												{t('documents.templates.columnUpdatedAt')}
											</Button>
										</TableHead>
										<TableHead className="w-10" />
									</TableRow>
								</TableHeader>
								<TableBody>
									{templates.map((tpl) => (
										<TableRow
											key={tpl.id}
											className="h-[72px] cursor-pointer"
											onClick={() => setPreviewTemplate(tpl)}
										>
											<TableCell className="px-4 text-sm font-[500] text-(--color-foreground)">
												{tpl.title}
											</TableCell>
											<TableCell className="px-4">
												<div className="flex items-center gap-2">
													<Avatar className="size-6">
														<AvatarFallback className="text-xs">{tpl.authorInitials}</AvatarFallback>
													</Avatar>
													<span className="text-sm text-(--color-foreground)">{tpl.authorName}</span>
												</div>
											</TableCell>
											<TableCell className="px-4 text-sm text-(--color-foreground)">
												{formatDate(tpl.createdAt)}
											</TableCell>
											<TableCell className="px-4 text-sm text-(--color-foreground)">
												{formatDate(tpl.updatedAt)}
											</TableCell>
											<TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
												<DropdownMenu>
													<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('documents.templates.actions.menu')} />}>
														<EllipsisVerticalIcon className="size-4" />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end" className="w-auto min-w-40">
														<DropdownMenuItem onClick={() => handleEditTemplate(tpl)}>
															<PencilIcon />
															{t('documents.templates.actions.edit')}
														</DropdownMenuItem>
														<DropdownMenuSeparator />
														<DropdownMenuItem variant="destructive" onClick={() => setTemplateToDelete(tpl)}>
															<Trash2Icon />
															{t('documents.templates.actions.delete')}
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
					<PanelDown total={total} page={page} onPageChange={setPage} limit={PAGE_SIZE} />
				</>
			)}

			<CreateTemplateDialog
				open={createOpen}
				onOpenChange={setCreateOpen}
				templates={templates}
				onNext={(title, base) => {
					setEditorName(title)
					setEditorBaseName(base?.title ?? '')
					setEditingTemplateId(null)
					setEditorBaseId(base?.id ?? '')
					setEditorType(base?.type ?? 'OTHER')
					setEditorInitialBlocks(base?.blocks ?? [])
					setEditorOpen(true)
				}}
			/>

			<TemplateEditor
				open={editorOpen}
				onOpenChange={setEditorOpen}
				templateName={editorName}
				baseTemplateName={editorBaseName}
				initialBlocks={editorInitialBlocks}
				onSave={handleEditorSave}
				isSaving={isCreating || isUpdating}
			/>

			<TemplatePreviewDialog
				template={previewTemplate}
				subtitle={previewTemplate ? t('documents.preview.subtitle', { date: formatDate(previewTemplate.createdAt) }) : undefined}
				versions={versions}
				history={history}
				totalPages={previewTemplate?.pagesCount ?? 1}
				open={!!previewTemplate}
				onOpenChange={(v) => { if (!v) setPreviewTemplate(null) }}
				onEdit={handlePreviewEdit}
				onDownloadPdf={handleDownloadPdf}
			/>

			<ConfirmDialog
				open={!!templateToDelete}
				onOpenChange={(v) => { if (!v) { setTemplateToDelete(null); setDeleteError(null) } }}
				title={t('documents.templates.actions.delete')}
				description={templateToDelete ? t('documents.templates.deleteConfirm', { title: templateToDelete.title }) : undefined}
				confirmLabel={t('documents.templates.actions.delete')}
				cancelLabel={t('documents.createDialog.cancel')}
				variant="destructive"
				loading={isDeleting}
				onConfirm={handleConfirmDelete}
			>
				{/* Ошибка удаления остаётся видимой в самом окне: шаблон,
				    использованный в документе, сервер удалить не даст. */}
				{deleteError && (
					<Alert variant="destructive">
						<AlertDescription>{deleteError}</AlertDescription>
					</Alert>
				)}
			</ConfirmDialog>
		</div>
	)
}
